import React, { Component } from "react";
import CartContext from "./CartContext";
import { BiRefresh } from "react-icons/bi";
import { MessageBox } from "./MessageBox";
import { LoadSpinner } from "./LoadSpinner";
import Swal from "sweetalert2";
import {
  Button,
  ButtonToolbar,
  Form,
  Table,
  Navbar,
  Nav,
  Modal,
} from "react-bootstrap";

export default class UsersWithRowEditAndModal extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      users: [],
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
      loadSpinnerOpen: false,

      suppliers: [],
      loadingSuppliers: false,

      showModal: false,
      modalMode: "add", // "add" or "edit"
      modalUser: {
        id: "00000000-0000-0000-0000-000000000000",
        storeId: this.context?.storeId || null,
        emailId: "",
        password: "",
        storeRoleId: 0,
        // storeRoleValue will hold either:
        // - role display text for non-supplier roles, OR
        // - supplier GUID string when role is Supplier
        storeRoleValue: "",
      },
    };
  }

  ShowLoadSpinner = () => this.setState({ loadSpinnerOpen: true });
  HideLoadSpinner = () => this.setState({ loadSpinnerOpen: false });

  CloseMessagBoxCallback = () => this.setState({ msgboxOpen: false });
  ShowMessagBox = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });

  componentDidMount() {
    this.GetSuppliers();
    this.GetUsers();
  }

  // fetch suppliers
  GetSuppliers = () => {
    this.setState({ loadingSuppliers: true });
    fetch(
      `${process.env.REACT_APP_API}Supplier/GetSuppliers/${this.context.storeId}`
    )
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        this.setState({ suppliers: list, loadingSuppliers: false });
      })
      .catch((err) => {
        console.error("GetSuppliers error", err);
        this.setState({ loadingSuppliers: false });
        this.ShowMessagBox("Error", "Failed to load suppliers");
      });
  };

  // fetch users
  GetUsers = async () => {
    try {
      this.ShowLoadSpinner();
      const res = await fetch(
        `${process.env.REACT_APP_API}AdminUsers/GetStoreUserAccounts/${this.context.storeId}`,
        {
          headers: {
            Authorization: `Bearer ${this.context.userData.bearertoken}`,
          },
        }
      );
      const data = await res.json();
      if (!data?.status) {
        // store whatever server returned; storeRoleValue may be name or GUID depending on previous saves
        const users = Array.isArray(data)
          ? data.filter((f) => f.storeRoleId !== 1)
          : [];
        this.setState({ users });
      }
    } catch (err) {
      Swal.fire(
        "Error",
        "Session Timeout! Please sign out and sign in again!",
        "error"
      );
    } finally {
      this.HideLoadSpinner();
    }
  };

  // normalize payload; storeRoleValue already contains GUID for Supplier role
  normalizeUserForSend = (u) => ({
    ...u,
    storeRoleId: u.storeRoleId ? Number(u.storeRoleId) : 0,
    storeId: u.storeId || this.context.storeId,
    storeRoleValue: u.storeRoleValue || "",
  });

  // detect Supplier role (by name fallback or id -> name)
  isSupplierRole = (roleIdOrName) => {
    if (!roleIdOrName) return false;
    if (typeof roleIdOrName === "string") {
      return roleIdOrName.toLowerCase() === "supplier";
    }
    const role = (this.context.storeRoles || []).find(
      (r) => r.id === Number(roleIdOrName)
    );
    return role ? role.name.toLowerCase() === "supplier" : false;
  };

  // open modal for add
  openAddModal = () => {
    this.setState({
      showModal: true,
      modalMode: "add",
      modalUser: {
        id: "00000000-0000-0000-0000-000000000000",
        storeId: this.context.storeId,
        emailId: "",
        password: "",
        storeRoleId: 0,
        storeRoleValue: "",
      },
    });
  };

  // open modal for edit — keep storeRoleValue as-is (may be GUID for supplier)
  openEditModal = (user) => {
    this.setState({
      showModal: true,
      modalMode: "edit",
      modalUser: { ...user },
    });
  };

  closeModal = () => this.setState({ showModal: false });

  // modal input changes — note: supplier dropdown uses name="storeRoleValue" and option values are GUIDs
  onModalChange = (e) => {
    const { name, value } = e.target;

    // when role changes, set storeRoleValue to role name; if role is Supplier, default to first supplier GUID (optional)
    if (name === "storeRoleId") {
      const roleId = Number(value);
      const role = (this.context.storeRoles || []).find((r) => r.id === roleId);
      const roleName = role ? role.name : "";
      this.setState((prev) => ({
        modalUser: {
          ...prev.modalUser,
          storeRoleId: roleId,
          // set role display (for non-supplier). For Supplier, overwrite below with GUID when user picks supplier.
          storeRoleValue: roleName,
        },
      }));

      // If new role is Supplier, set storeRoleValue to first supplier GUID as a default (optional)
      if (roleName && roleName.toLowerCase() === "supplier") {
        const first = this.state.suppliers[0];
        if (first) {
          this.setState((prev) => ({
            modalUser: { ...prev.modalUser, storeRoleValue: String(first.id) },
          }));
        }
      }
      return;
    }

    // when selecting supplier from Config dropdown -> write GUID directly into storeRoleValue
    if (
      name === "storeRoleValue" &&
      this.isSupplierRole(
        this.state.modalUser.storeRoleId || this.state.modalUser.storeRoleValue
      )
    ) {
      // value is supplier GUID string
      this.setState((prev) => ({
        modalUser: { ...prev.modalUser, storeRoleValue: value },
      }));
      return;
    }

    // otherwise normal set
    this.setState((prev) => ({
      modalUser: { ...prev.modalUser, [name]: value },
    }));
  };

  submitModal = async () => {
    const u = { ...this.state.modalUser };

    // basic validation
    if (!u.emailId || !u.password || !u.storeRoleId) {
      Swal.fire("Error", "Email, Password and Role are required.", "error");
      return;
    }
    if (u.password.length < 6) {
      Swal.fire("Error", "Password must be at least 6 characters.", "error");
      return;
    }

    // if role is Supplier, storeRoleValue must be a GUID (selected supplier)
    const isSupplier = this.isSupplierRole(u.storeRoleId || u.storeRoleValue);
    if (isSupplier) {
      if (!u.storeRoleValue || !String(u.storeRoleValue).trim()) {
        Swal.fire(
          "Error",
          "Config (select a supplier) is required for Supplier role.",
          "error"
        );
        return;
      }
      // ensure it's string GUID — backend expects string in StoreRoleValue
      u.storeRoleValue = String(u.storeRoleValue);
    }

    try {
      this.ShowLoadSpinner();
      const payload = [this.normalizeUserForSend(u)];
      const res = await fetch(
        `${process.env.REACT_APP_API}AdminUsers/AddOrUpdateStoreUsers/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.context.userData.bearertoken}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        await this.GetUsers();
        this.closeModal();
        Swal.fire(
          "Success",
          this.state.modalMode === "add" ? "User added" : "User updated",
          "success"
        );
      } else {
        const txt = await res.text();
        Swal.fire("Error", `Save failed: ${txt}`, "error");
      }
    } catch (err) {
      Swal.fire("Error", "Save failed: " + err, "error");
    } finally {
      this.HideLoadSpinner();
    }
  };

  // delete
  OnDelete = (i) => {
    const user = this.state.users[i];
    if (!user?.id) {
      this.ShowMessagBox("Info", "Invalid row, please refresh!");
      return;
    }
    Swal.fire({
      title: "Delete User?",
      text: "This will delete the user permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (res) => {
      if (!res.isConfirmed) return;
      try {
        this.ShowLoadSpinner();
        const resp = await fetch(
          `${process.env.REACT_APP_API}AdminUsers/DeleteUser/${user.id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${this.context.userData.bearertoken}`,
            },
          }
        );
        if (resp.ok) {
          await this.GetUsers();
          Swal.fire("Deleted", "User removed successfully!", "success");
        } else {
          const txt = await resp.text();
          Swal.fire("Error", `Delete failed: ${txt}`, "error");
        }
      } catch (err) {
        Swal.fire("Error", "Delete failed due to network error!", "error");
      } finally {
        this.HideLoadSpinner();
      }
    });
  };

  // render one row: show Config only for Supplier role (resolve GUID -> name)
  renderRow = (u, i) => {
    const key = u.id || `row-${i}`;
    const isSupplierRow = this.isSupplierRole(
      u.storeRoleId || u.storeRoleValue
    );

    let configDisplay = "";
    if (isSupplierRow) {
      const guid = String(u.storeRoleValue || "").trim();
      const sup = (this.state.suppliers || []).find(
        (s) => String(s.id) === guid
      );
      configDisplay = sup ? sup.name : guid || "";
    }

    const roleDisplay =
      this.context.storeRoles.find((r) => r.id === u.storeRoleId)?.name ||
      u.storeRoleValue ||
      "";

    return (
      <tr key={key}>
        <td>{i + 1}</td>
        <td>{u.emailId}</td>
        <td>
          <Form.Control type="password" readOnly value={u.password || ""} />
        </td>
        <td>{roleDisplay}</td>
        <td>{configDisplay}</td>
        <td className="text-center">
          <Button
            variant="primary"
            size="sm"
            className="me-1"
            onClick={() => this.openEditModal(u)}
          >
            Edit
          </Button>
          <Button variant="danger" size="sm" onClick={() => this.OnDelete(i)}>
            🗑 Delete
          </Button>
        </td>
      </tr>
    );
  };

  render() {
    const m = this.state.modalUser;
    const isModalSupplier = this.isSupplierRole(
      m.storeRoleId || m.storeRoleValue
    );

    return (
      <>
        {this.state.loadSpinnerOpen && <LoadSpinner open="true" />}
        {this.state.msgboxOpen && (
          <MessageBox
            open={this.state.msgboxOpen}
            title={this.state.msgboxTitle}
            message={this.state.msgboxMessage}
            callback={this.CloseMessagBoxCallback}
          />
        )}

        <div className="container-fluid p-0">
          <Navbar className="shadow position-sticky fixed-top py-2 bg-white">
            <h3 className="mb-0">Users</h3>
            <Navbar.Collapse className="justify-content-end">
              <Nav>
                <ButtonToolbar>
                  <Button
                    className="mainthemecolor"
                    onClick={this.openAddModal}
                  >
                    + Add
                  </Button>
                  &nbsp;
                  <Button variant="success" onClick={this.GetUsers}>
                    <BiRefresh size={18} /> Refresh
                  </Button>
                </ButtonToolbar>
              </Nav>
            </Navbar.Collapse>
          </Navbar>

          <div className="p-3 mt-5">
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <Table striped bordered hover className="bg-white">
                <thead
                  className="table-light"
                  style={{ position: "sticky", top: 0, zIndex: 1 }}
                >
                  <tr>
                    <th>#</th>
                    <th>UserId</th>
                    <th>Password</th>
                    <th>Role</th>
                    <th>Config</th>
                    <th className="text-center">Options</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.users.map((u, i) => this.renderRow(u, i))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>

        {/* Add / Edit Modal */}
        <Modal show={this.state.showModal} onHide={this.closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {this.state.modalMode === "add" ? "Add User" : "Edit User"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>UserId (Email)</Form.Label>
                <Form.Control
                  name="emailId"
                  value={m.emailId}
                  onChange={this.onModalChange}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  name="password"
                  type="password"
                  value={m.password}
                  onChange={this.onModalChange}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  name="storeRoleId"
                  value={m.storeRoleId}
                  onChange={this.onModalChange}
                >
                  <option value={0}></option>
                  {this.context.storeRoles
                    .filter((r) => r.name !== "ProductAdmin")
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                </Form.Select>

                {/* Config (supplier dropdown) shown only when role is Supplier */}
                {isModalSupplier && (
                  <Form.Group className="mt-2">
                    <Form.Label>Config</Form.Label>
                    <Form.Select
                      name="storeRoleValue"
                      value={m.storeRoleValue || ""}
                      onChange={this.onModalChange}
                    >
                      <option value="">-- select supplier --</option>
                      {this.state.suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                    <small className="text-muted">
                      Selected supplier GUID will be saved to StoreRoleValue.
                    </small>
                  </Form.Group>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.submitModal}>
              {this.state.modalMode === "add" ? "Add" : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}
